import React from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import IEventBaseDataProps from './IEventBaseDataProps';
import { useNavigate } from 'react-router-dom';

import EditionTableToolbar from '../../common/EditionTableToolbar';
import IEditableRider from '../../../models/IEditableRider';
import { IGroup } from '../../../models/IEvent';
import ConfirmDialog from '../../common/ConfirmDialog';
import { format } from 'util';
import validator from 'validator';
import Alert from '@mui/material/Alert';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 280,
        },
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
    }));

const showFromMdTableCellSx = { display: { xs: 'none', md: 'table-cell' } } as const;
const showFromLgTableCellSx = { display: { xs: 'none', lg: 'table-cell' } } as const;

interface IParticipantErrors {
    name?: string;
    number?: string;
    tagId?: string;
    email?: string;
    additionalTagsInUse?: string[];
}
interface IEditParticipantDialogProps {
    participant: IEditableRider,
    allParticipants: IEditableRider[],
    groups: IGroup[];
    allActive: boolean;
    onCompleteEdit: (participant: IUpdatedRider) => void;
    onCancelEdit: () => void;
}

function EditParticipantDialog(props: IEditParticipantDialogProps) {
    const { t } = useTranslation("common");
    const [currentParticipant, setCurrentParticipant] = React.useState<IEditableRider>(props.participant);
    const [linkTag, setLinkTag] = React.useState<string>('');
    const [errors, setErrors] = React.useState<IParticipantErrors>();
    const [ridersToDelete, setRidersToDelete] = React.useState<string[]>([]);
    const [pendingLinkTag, setPendingLinkTag] = React.useState<{ tag: string; conflictingRider: IEditableRider } | null>(null);
    const [pendingUnlinkTag, setPendingUnlinkTag] = React.useState<string | null>(null);
    const originalTagId = props.participant.tagId;
    const handleClose = () => {
        props.onCancelEdit();
    }
    const handleCompleteEdit = () => {

        props.onCompleteEdit({ originalTagId: originalTagId, ridersToDelete, ...currentParticipant });
    }

    const checkParticipant = (participantToValidate: IEditableRider) => {
        const error: IParticipantErrors = {};
        let anyError = false;
        if (participantToValidate) {
            if (participantToValidate.tagId.length === 0) {
                error.tagId = format(t("commonErrors.requiredField"), t("rider.tag"));
                anyError = true;
            }
            if (participantToValidate.name.length === 0) {
                error.name = format(t("commonErrors.requiredField"), t("rider.name"));
                anyError = true;
            }
            if (participantToValidate.tagId !== originalTagId) {
                if (props.allParticipants.findIndex(rider => rider.tagId === participantToValidate.tagId) !== -1) {
                    error.tagId = t<string>("rider.errorTagInUse");
                    anyError = true;
                }
            }
            if (props.allParticipants.findIndex(rider => rider.tagId !== participantToValidate.tagId
                && rider.tagId !== originalTagId
                && rider.name.localeCompare(participantToValidate.name, ['es'], { sensitivity: 'base' }) === 0) !== -1) {
                error.name = t<string>("rider.errorNameInUse");
                anyError = true;
            }
            if (participantToValidate.email?.length > 0) {
                if (validator.isEmail(participantToValidate.email) === false) {
                    error.email = t<string>("rider.errorInvalidEmail");
                    anyError = true;
                }
            }
            if (participantToValidate.linkedTags?.length > 0) {
                error.additionalTagsInUse = participantToValidate.linkedTags.filter(tagId =>
                    props.allParticipants.findIndex(r => r.tagId !== participantToValidate.tagId &&
                        (r.tagId === tagId || (r.linkedTags.length > 0 && r.linkedTags.indexOf(tagId) !== -1))) !== -1);
            }
        }
        if (anyError === true) {
            setErrors(error);
        } else {
            setErrors(undefined);
        }
    }
    return (<React.Fragment>
    <Dialog open={true} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t("rider.rider")}</DialogTitle>
        <DialogContent>

            <TextField
                autoFocus
                error={Boolean(errors?.name)}
                helperText={errors?.name}
                margin="dense"
                id="name"
                label={t("rider.name")}
                type="text"
                fullWidth
                value={currentParticipant.name}
                onChange={e => {
                    const newParticipant: IEditableRider = { ...currentParticipant, name: e.target.value };
                    setCurrentParticipant(newParticipant);
                    checkParticipant(newParticipant);
                }}
            />
            <TextField
                error={Boolean(errors?.number)}
                helperText={errors?.number}
                margin="dense"
                id="number"
                label={t("rider.number")}
                type="text"
                value={currentParticipant.number}
                onChange={e => {
                    const newParticipant: IEditableRider = { ...currentParticipant, number: e.target.value };
                    setCurrentParticipant(newParticipant);
                    checkParticipant(newParticipant);
                }}
            />
            <TextField
                error={Boolean(errors?.tagId)}
                helperText={errors?.tagId}
                margin="dense"
                id="tagId"
                label={t("rider.tag")}
                type="number"
                value={currentParticipant.tagId}
                onChange={e => {

                    const newParticipant: IEditableRider = { ...currentParticipant, tagId: e.target.value };
                    setCurrentParticipant(newParticipant);
                    checkParticipant(newParticipant);
                }}
            />
            <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel id="group-label">{t("rider.group")}</InputLabel>
                <Select
                    label={t("rider.group")}
                    value={currentParticipant.group}
                    onChange={e => {
                        const newParticipant: IEditableRider = { ...currentParticipant, group: typeof e.target.value === 'string' ? e.target.value : '' };
                        setCurrentParticipant(newParticipant);
                        checkParticipant(newParticipant);
                    }}            >
                    {props.groups.map(g =>
                        (<MenuItem value={g.groupId}>{g.name}</MenuItem>)
                    )}
                </Select>
            </FormControl>
            <TextField
                margin="dense"
                id="vehicleModel"
                label={t("rider.vehicleModel")}
                type="text"
                fullWidth
                value={currentParticipant.vehicleModel}
                onChange={e => {
                    const newParticipant: IEditableRider = { ...currentParticipant, vehicleModel: e.target.value };
                    setCurrentParticipant(newParticipant);
                    checkParticipant(newParticipant);
                }}
            />
            <TextField
                error={Boolean(errors?.email)}
                helperText={errors?.email}
                margin="dense"
                id="email"
                label={t("rider.email")}
                type="email"
                fullWidth
                value={currentParticipant.email}
                onChange={e => {
                    const newParticipant: IEditableRider = { ...currentParticipant, email: e.target.value };
                    setCurrentParticipant(newParticipant);
                    checkParticipant(newParticipant);
                }}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={currentParticipant.trial}
                        onChange={e => {
                            const newParticipant: IEditableRider = { ...currentParticipant, trial: e.target.checked };
                            setCurrentParticipant(newParticipant);
                            checkParticipant(newParticipant);
                        }}
                    />
                }
                label={t("rider.trial")}
            />
            <Divider />
            <TextField
                margin="dense"
                id="linkedTag"
                label={t("rider.linkedTag")}
                type="number"
                value={linkTag}
                onChange={e => setLinkTag(e.target.value)}
            />
            <Button onClick={e => {
                const lts = currentParticipant.linkedTags;
                if (linkTag !== currentParticipant.tagId && lts.indexOf(linkTag) === -1) {
                    const conflictingRider = props.allParticipants.find(r =>
                        r.tagId !== currentParticipant.tagId &&
                        (r.tagId === linkTag || r.linkedTags.includes(linkTag)));
                    if (conflictingRider) {
                        setPendingLinkTag({ tag: linkTag, conflictingRider });
                    } else {
                        lts.push(linkTag);
                        const newParticipant: IEditableRider = { ...currentParticipant, linkedTags: lts };
                        setCurrentParticipant(newParticipant);
                        checkParticipant(newParticipant);
                        setLinkTag('');
                    }
                }
            }} color="primary">
                {t("rider.linkTag")}
            </Button>
            <List dense>
                {currentParticipant.linkedTags.map(tag => {
                    return (<ListItem key={tag}>
                        <ListItemText>{tag}</ListItemText>
                        <ListItemSecondaryAction>
                            <Tooltip title={t("rider.unlinkTag")}>
                                <IconButton edge="end" size="small" onClick={() => {
                                    setPendingUnlinkTag(tag);
                                }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </ListItemSecondaryAction>
                    </ListItem>);
                })}
            </List>

            {errors?.additionalTagsInUse && errors.additionalTagsInUse.length > 0 ?
                (<Alert severity="warning">{format(t("rider.warningLinkedTagsInUse"), errors?.additionalTagsInUse?.join(','))}</Alert>)
                : (<></>)}


        </DialogContent>
        <DialogActions>
            <Button onClick={handleCompleteEdit} color="primary" disabled={Boolean(errors)}>
                {t("commonActions.ok")}
            </Button>
            <Button onClick={handleClose} color="primary">
                {t("commonActions.cancel")}
            </Button>
        </DialogActions>
    </Dialog>
    <ConfirmDialog
        open={pendingLinkTag !== null}
        title={t("rider.confirmLinkTagTitle")}
        message={format(t("rider.confirmLinkTagConflict"), pendingLinkTag?.tag ?? '', pendingLinkTag?.conflictingRider?.name ?? '')}
        onAccept={() => {
            if (pendingLinkTag) {
                const newLinkedTags = [...currentParticipant.linkedTags, pendingLinkTag.tag];
                const newParticipant: IEditableRider = { ...currentParticipant, linkedTags: newLinkedTags };
                setCurrentParticipant(newParticipant);
                checkParticipant(newParticipant);
                setRidersToDelete(prev => [...prev, pendingLinkTag.conflictingRider.tagId]);
                setLinkTag('');
                setPendingLinkTag(null);
            }
        }}
        onCancel={() => setPendingLinkTag(null)}
    />
    <ConfirmDialog
        open={pendingUnlinkTag !== null}
        title={t("rider.confirmUnlinkTagTitle")}
        message={format(t("rider.confirmUnlinkTag"), pendingUnlinkTag ?? '')}
        onAccept={() => {
            if (pendingUnlinkTag) {
                const newLinkedTags = currentParticipant.linkedTags.filter(lt => lt !== pendingUnlinkTag);
                const newParticipant: IEditableRider = { ...currentParticipant, linkedTags: newLinkedTags };
                setCurrentParticipant(newParticipant);
                checkParticipant(newParticipant);
                setPendingUnlinkTag(null);
            }
        }}
        onCancel={() => setPendingUnlinkTag(null)}
    />
    </React.Fragment>);
}

interface IEventParticipantsData {
    pilots: IEditableRider[];
}

interface IUpdatedRider extends IEditableRider {
    originalTagId: string;
    ridersToDelete?: string[];
}

interface IEventParticipantsProps extends IEventBaseDataProps<IEventParticipantsData> {
    groups: IGroup[];
    allActive: boolean;
    raceId: string;
    isAdmin?: boolean;
    onNew: (value: IEditableRider) => void;
    onUpdate: (value: IUpdatedRider) => void;
    onDelete: (deleteTags: string[]) => void;
    onRecalculateRider?: (tagId: string) => void;
}

function EventParticipants(props: IEventParticipantsProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [selected, setSelected] = React.useState<string[]>([]);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [editingParticipant, setEditingParticipant] = React.useState<IEditableRider | undefined>(undefined);
    const [riders, setRiders] = React.useState<IEditableRider[]>(props.data.pilots);
    const [currentFilter, setCurrentFilter] = React.useState<string>('');
    const rowCount = props.data.pilots?.length ?? 0;

    React.useEffect(() => {
        const pilots = props.data.pilots ?? [];
        if (currentFilter.length > 0) {
            setRiders(pilots.filter(r =>
                (r.name || '').toLowerCase().includes(currentFilter)
                || (r.tagId || '').toLowerCase().includes(currentFilter)
                || (r.vehicleModel || '').toLowerCase().includes(currentFilter)
                || (r.number || '').toLowerCase().includes(currentFilter)
                || (r.email || '').toLowerCase().includes(currentFilter)));
        } else {
            setRiders(pilots);
        }
    }, [props.data.pilots, currentFilter]);
    const groupMap = new Map(props.groups.map(g => [g.groupId, g.name]));

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };
    const handleDoubleClick = (event: React.MouseEvent<unknown>, groupId: string) => {
        editParticipant(groupId);
    }

    const handleEdit = (event: React.MouseEvent<unknown>) => {

        if (selected.length === 1) {
            editParticipant(selected[0]);
        }
    }

    const editParticipant = (tagIdFriendly: string) => {
        const participant = props.data.pilots.find(p => p.tagId === tagIdFriendly);
        if (participant) {
            setEditingParticipant(participant);
        } else {
            console.log('no participant');
        }
    }


    const defaultGroup = (): (IGroup | undefined) => {
        if (props.groups.length > 0) {
            return props.groups.sort((g1: IGroup, g2: IGroup) => g1.targetTime - g2.targetTime)[0];
        } else {
            return undefined;
        }
    }
    const getGroupName = (groupId: string) => {
        return groupMap.get(groupId) ?? t("rider.invalidGroup");
    }

    const handleAdd = () => {

        const newParticipant: IEditableRider = {
            name: '',
            achieved: false,
            linkedTags: [],
            number: '',
            email: '',
            registered: false,
            riderAvatarUrl: '',
            started: false,
            trial: false,
            tagId: '9999',
            vehicleModel: '',
            userId: '',
            group: defaultGroup()?.groupId ?? ''
        };
        setEditingParticipant(newParticipant);
    }

    const handleCompleteEdit = (participant: IUpdatedRider) => {
        if (participant.originalTagId !== participant.tagId) {
            if (props.data.pilots.findIndex(r => r.tagId === participant.tagId) !== -1) {
                // the tag is already in use
            }
        }
        if (participant.ridersToDelete && participant.ridersToDelete.length > 0) {
            props.onDelete(participant.ridersToDelete);
        }
        props.onUpdate(participant);
        setEditingParticipant(undefined);
    }


    const handleDelete = (event: React.MouseEvent<unknown>) => {
        setOpenConfirmation(true);
    }

    const deleteSelectedParticipants = () => {
        setOpenConfirmation(false);

        setSelected([]);
        if (props.onDelete) {
            props.onDelete(selected);
        }
    }
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = riders.map((n) => n.tagId);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };
    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    const handleCloseConfirmation = () => {
        setOpenConfirmation(false);
    };
    const handleFilter = (event: any) => {
        setCurrentFilter(event.target.value.toLowerCase());
    }

    const handleReadsClick = (event: React.MouseEvent<unknown>, tagId: string) => {
        event.stopPropagation();
        navigate(`/admin/rider/${props.raceId}/${tagId}/reads`);
    };

    return (
        <React.Fragment>

            <ConfirmDialog
                open={openConfirmation}
                onAccept={deleteSelectedParticipants}
                onCancel={handleCloseConfirmation}
                title={t("rider.deleteRiders")}
                message={format(t("rider.confirmDelete"), selected.length.toString())} />

            {editingParticipant === undefined ? (<></>) : (<EditParticipantDialog
                participant={editingParticipant}
                allParticipants={props.data.pilots}
                allActive={props.allActive}
                groups={props.groups}
                onCompleteEdit={handleCompleteEdit}
                onCancelEdit={() => setEditingParticipant(undefined)} />)}
            <Paper className={classes.paper}>

                <EditionTableToolbar
                    numSelected={selected.length}
                    headerLabel={t("eventsetup.participants")}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAdd={handleAdd}
                    onFilterChanged={handleFilter} />
                <TableContainer component={Paper}>
                    <Table className={classes.table} aria-label="participants">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < rowCount}
                                        checked={rowCount > 0 && selected.length === rowCount}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={showFromLgTableCellSx}>{t("rider.number")}</TableCell>
                                <TableCell align="left" >{t("rider.name")}</TableCell>
                                <TableCell align="right">{t("rider.tag")}</TableCell>
                                <TableCell align="left" sx={showFromMdTableCellSx}>{t("rider.group")}</TableCell>
                                <TableCell align="left" sx={showFromLgTableCellSx}>{t("rider.vehicleModel")}</TableCell>
                                <TableCell align="left" sx={showFromLgTableCellSx}>{t("rider.email")}</TableCell>
                                {props.allActive === false ? (<TableCell sx={showFromLgTableCellSx}>{t("rider.trial")}</TableCell>) : <></>}
                                <TableCell sx={showFromLgTableCellSx}>{t("rider.linkedTags")}</TableCell>
                                {props.isAdmin && <TableCell padding="none" sx={{ width: '100px' }} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {riders.map((participant, index) => {
                                const isItemSelected = isSelected(participant.tagId);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <TableRow key={participant.tagId}
                                        hover
                                        onClick={(event) => handleClick(event, participant.tagId)}
                                        onDoubleClick={(event) => handleDoubleClick(event, participant.tagId)}
                                        role="checkbox"
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isItemSelected}
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={showFromLgTableCellSx}>{participant.number}</TableCell>
                                        <TableCell component="th" scope="row" id={labelId}>
                                            {participant.name}
                                        </TableCell>
                                        <TableCell align="right">{participant.tagId}</TableCell>
                                        <TableCell align="left" sx={showFromMdTableCellSx}>{getGroupName(participant.group)}</TableCell>
                                        <TableCell align="left" sx={showFromLgTableCellSx}>{participant.vehicleModel}</TableCell>
                                        <TableCell align="left" sx={showFromLgTableCellSx}>{participant.email}</TableCell>
                                        {props.allActive === false ? (<TableCell sx={showFromLgTableCellSx}>
                                            <Checkbox checked={!participant.trial} disabled inputProps={{ 'aria-label': t("rider.trial") || "trial" }} />
                                        </TableCell>) : <></>}
                                        <TableCell sx={showFromLgTableCellSx}>{participant.linkedTags.length}</TableCell>
                                        {props.isAdmin && (
                                            <TableCell align="center" padding="none">
                                                <Tooltip title={t('riderReads.manageReads') as string}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(event) => handleReadsClick(event, participant.tagId)}
                                                        aria-label={t('riderReads.manageReads') as string}
                                                    >
                                                        <HistoryIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {props.onRecalculateRider && (
                                                    <Tooltip title={t('eventsetup.recalculateRider') as string}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                props.onRecalculateRider!(participant.tagId);
                                                            }}
                                                            aria-label={t('eventsetup.recalculateRider') as string}
                                                        >
                                                            <RefreshIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </React.Fragment>
    );
}

export default EventParticipants;